import { AlertTriangle } from 'lucide-react'

export interface AssistantConfirmationPromptProps {
  /** Server-authored: the title and details were composed from what the store actually holds. */
  request: { title: string; details: Record<string, string> }
  onAnswer: (confirmed: boolean) => void
}

/**
 * The Yes/No question, rendered INSIDE the conversation — above the composer,
 * below the transcript — and not as a browser modal.
 *
 * That placement is the standing decision recorded on `navigate_to`'s
 * `discardUnsaved` flag: a destructive confirmation is "negotiated in the
 * conversation, where the user can see the question and the assistant can see
 * the answer — instead of a browser modal the assistant is blind to". The
 * answer leaves here as a tool result, so the assistant sees it as data.
 *
 * Props-driven and free of i18n, like `AssistantRateLimitBanner` beside it: the
 * text it frames is composed server-side from the record's own field names, and
 * translating only the two buttons would produce a half-translated question
 * about a deletion.
 *
 * Neither button is autofocused. "Yes" here is irreversible, and a focused
 * default is one stray Enter away from being pressed by someone who was typing.
 */
export function AssistantConfirmationPrompt({ request, onAnswer }: AssistantConfirmationPromptProps) {
  const rows = Object.entries(request.details)

  return (
    <div
      role="group"
      aria-label="Confirmation required"
      data-testid="assistant-confirmation"
      className="border-t border-amber-300 bg-amber-50 px-[0.75em] py-[0.625em] text-[0.72em] text-amber-900 dark:border-amber-700 dark:bg-amber-900/25 dark:text-amber-100"
    >
      <p className="flex items-start gap-[0.375em] font-semibold">
        <AlertTriangle className="mt-[0.1em] h-[1.1em] w-[1.1em] shrink-0" aria-hidden="true" />
        <span>{request.title}</span>
      </p>
      {rows.length > 0 && (
        <dl className="mt-[0.5em] grid grid-cols-[minmax(0,8em)_1fr] gap-x-[0.5em] gap-y-[0.125em]">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="truncate opacity-70">{label}</dt>
              <dd className="break-words font-medium">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="mt-[0.625em] flex items-center gap-[0.5em]">
        <button
          type="button"
          onClick={() => onAnswer(true)}
          data-testid="assistant-confirmation-yes"
          className="rounded bg-red-600 px-[0.75em] py-[0.25em] font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400"
        >
          Yes, do it
        </button>
        <button
          type="button"
          onClick={() => onAnswer(false)}
          data-testid="assistant-confirmation-no"
          className="rounded border border-amber-400 px-[0.75em] py-[0.25em] font-medium hover:bg-amber-100 dark:border-amber-600 dark:hover:bg-amber-800/40"
        >
          No, cancel
        </button>
      </div>
    </div>
  )
}
